import { defineMessages } from '../define';

/**
 * AI analysis screen.
 *
 * Provider and model names (OpenAI, Anthropic, Gemini, OpenRouter, and every
 * model id) stay in the page as literals: they are proper nouns and, in the
 * case of a model id, the exact string the API expects.
 */
export const ai = defineMessages({
  en: {
    'ai.off.title': 'AI is not available yet',
    'ai.off.flag': 'The AI feature flag is off for this deployment. Set AI_ENABLED=true in the environment and restart the API and worker.',
    'ai.off.noKeys': 'No provider key is stored yet. Configure one below and it will turn on straight away.',
    'ai.title': 'AI analysis',
    'ai.description':
      'Optional enrichment of incoming messages, run through a provider you supply the key for.',
    'ai.requestFailed':
      'The request could not be completed. Check that the gateway is reachable.',

    'ai.notice.title': 'Message content leaves this server when AI is on.',
    'ai.notice.body':
      'Analysing a message means sending its text to the provider you configure below. That is a third party, on their infrastructure, under their retention policy. AI is off by default and stays off until you add a key — no analysis runs, and nothing is sent, until then.',

    'ai.providers': 'Providers',
    'ai.keyConfigured': 'Key configured',
    'ai.notConfigured': 'Not configured',
    'ai.configure': 'Configure',

    'ai.tasks.title': 'What it does',
    'ai.tasks.description':
      'Each task is a single call with the message or conversation as untrusted input.',
    'ai.task.summarise': 'Summarise',
    'ai.task.summarise.detail':
      'Condenses a conversation into a few sentences so you can pick it up mid-thread.',
    'ai.task.intent': 'Intent',
    'ai.task.intent.detail':
      'Labels what the sender wants — a purchase enquiry, a complaint, a delivery chase.',
    'ai.task.sentiment': 'Sentiment',
    'ai.task.sentiment.detail':
      'Marks a message positive, neutral or negative. Useful mostly in aggregate.',
    'ai.task.spam': 'Spam',
    'ai.task.spam.detail': 'Scores how likely a message is a scam or a mass advert, from 0 to 1.',
    'ai.task.category': 'Category',
    'ai.task.category.detail':
      'Sorts a message into a coarse bucket such as sales, support or billing.',
    'ai.task.autoReply': 'Auto-reply',
    'ai.task.autoReply.detail':
      'Drafts the next reply for an operator to read, edit and send. Nothing is sent for you.',

    'ai.dialog.title': 'Configure {name}',
    'ai.dialog.fallbackName': 'provider',
    'ai.dialog.description': 'The key is encrypted at rest and never returned by the API.',
    'ai.apiKey': 'API key',
    'ai.key.stored': 'A key is stored. Click the field to replace it; leave it alone to keep it.',
    'ai.key.hint.openai': 'Starts with sk-.',
    'ai.key.hint.anthropic': 'Starts with sk-ant-.',
    'ai.key.hint.gemini': 'A Google AI Studio API key.',
    'ai.key.hint.openrouter': 'Starts with sk-or-.',
    'ai.model': 'Model',
    'ai.baseUrl': 'Base URL',
    'ai.baseUrl.hint': 'Optional. Set it to reach a proxy or a compatible self-hosted gateway.',

    'ai.updated': '{name} updated',
    'ai.keyRemoved': '{name} key removed',
    'ai.removeKey': 'Remove key',
    'ai.remove.title': 'Remove the {name} key?',
    'ai.remove.description':
      'Tasks using this provider stop running until a key is added again.',
  },
  th: {
    'ai.off.title': 'ยังใช้งาน AI ไม่ได้',
    'ai.off.flag': 'สวิตช์ AI ของระบบนี้ยังปิดอยู่ ต้องตั้ง AI_ENABLED=true ในไฟล์ตั้งค่าแล้วรีสตาร์ท API และ worker',
    'ai.off.noKeys': 'ยังไม่มี key ของผู้ให้บริการเลย ตั้งค่าด้านล่างแล้วจะเปิดใช้งานได้ทันที',
    'ai.title': 'การวิเคราะห์ด้วย AI',
    'ai.description':
      'ตัวเลือกเสริมสำหรับวิเคราะห์ข้อความที่เข้ามา โดยเรียกผ่านผู้ให้บริการที่คุณใส่คีย์ไว้เอง',
    'ai.requestFailed': 'ดำเนินการตามคำขอไม่สำเร็จ กรุณาตรวจสอบว่าเกตเวย์เข้าถึงได้',

    'ai.notice.title': 'เมื่อเปิด AI เนื้อหาข้อความจะถูกส่งออกนอกเซิร์ฟเวอร์นี้',
    'ai.notice.body':
      'การวิเคราะห์ข้อความคือการส่งเนื้อหาข้อความไปยังผู้ให้บริการที่คุณตั้งค่าไว้ด้านล่าง ซึ่งเป็นบุคคลที่สาม อยู่บนระบบของเขา และอยู่ภายใต้นโยบายการเก็บข้อมูลของเขา AI ถูกปิดไว้เป็นค่าเริ่มต้นและจะยังปิดอยู่จนกว่าคุณจะเพิ่มคีย์ ก่อนหน้านั้นจะไม่มีการวิเคราะห์และไม่มีข้อมูลใดถูกส่งออก',

    'ai.providers': 'ผู้ให้บริการ',
    'ai.keyConfigured': 'ตั้งค่าคีย์แล้ว',
    'ai.notConfigured': 'ยังไม่ได้ตั้งค่า',
    'ai.configure': 'ตั้งค่า',

    'ai.tasks.title': 'ทำอะไรได้บ้าง',
    'ai.tasks.description':
      'แต่ละงานคือการเรียกหนึ่งครั้ง โดยใช้ข้อความหรือบทสนทนาเป็นข้อมูลนำเข้าที่ยังไม่ผ่านการตรวจสอบ',
    'ai.task.summarise': 'สรุปความ',
    'ai.task.summarise.detail': 'ย่อบทสนทนาให้เหลือไม่กี่ประโยค เพื่อให้คุณตามเรื่องกลางทางได้',
    'ai.task.intent': 'เจตนา',
    'ai.task.intent.detail':
      'ระบุว่าผู้ส่งต้องการอะไร เช่น สอบถามเพื่อซื้อ ร้องเรียน หรือติดตามการจัดส่ง',
    'ai.task.sentiment': 'อารมณ์ความรู้สึก',
    'ai.task.sentiment.detail':
      'ระบุว่าข้อความเป็นเชิงบวก กลาง หรือลบ มีประโยชน์เมื่อดูเป็นภาพรวมมากกว่ารายข้อความ',
    'ai.task.spam': 'สแปม',
    'ai.task.spam.detail':
      'ให้คะแนนความเป็นไปได้ว่าข้อความเป็นการหลอกลวงหรือโฆษณาหว่าน ตั้งแต่ 0 ถึง 1',
    'ai.task.category': 'หมวดหมู่',
    'ai.task.category.detail': 'จัดข้อความเข้ากลุ่มกว้าง ๆ เช่น การขาย การบริการ หรือการเงิน',
    'ai.task.autoReply': 'ร่างข้อความตอบกลับ',
    'ai.task.autoReply.detail':
      'ร่างคำตอบถัดไปให้ผู้ปฏิบัติงานอ่าน แก้ไข แล้วกดส่งเอง ระบบจะไม่ส่งให้อัตโนมัติ',

    'ai.dialog.title': 'ตั้งค่า {name}',
    'ai.dialog.fallbackName': 'ผู้ให้บริการ',
    'ai.dialog.description': 'คีย์จะถูกเข้ารหัสไว้ และ API จะไม่ส่งค่ากลับมา',
    'ai.apiKey': 'API key',
    'ai.key.stored': 'มีคีย์เก็บไว้แล้ว คลิกที่ช่องนี้เพื่อใส่คีย์ใหม่ หรือปล่อยไว้เพื่อใช้คีย์เดิม',
    'ai.key.hint.openai': 'ขึ้นต้นด้วย sk-',
    'ai.key.hint.anthropic': 'ขึ้นต้นด้วย sk-ant-',
    'ai.key.hint.gemini': 'API key จาก Google AI Studio',
    'ai.key.hint.openrouter': 'ขึ้นต้นด้วย sk-or-',
    'ai.model': 'โมเดล',
    'ai.baseUrl': 'Base URL',
    'ai.baseUrl.hint':
      'ไม่บังคับ ตั้งค่าเมื่อต้องการเรียกผ่านพร็อกซีหรือเกตเวย์ที่ติดตั้งเองและใช้งานร่วมกันได้',

    'ai.updated': 'อัปเดต {name} แล้ว',
    'ai.keyRemoved': 'ลบคีย์ของ {name} แล้ว',
    'ai.removeKey': 'ลบคีย์',
    'ai.remove.title': 'ลบคีย์ของ {name} ใช่หรือไม่',
    'ai.remove.description': 'งานที่ใช้ผู้ให้บริการนี้จะหยุดทำงานจนกว่าจะเพิ่มคีย์อีกครั้ง',
  },
});
